/**
 * src/components/spatial/countryBounds.ts
 *
 * Light helper that derives a (bbox, centroid) tuple from the GeoTrust
 * countries FeatureCollection that App.tsx loads at startup. The
 * SpatialObjectsCountrySection uses these values to:
 *   - filter spatial objects to the active country (`bbox` query),
 *   - compute "nearby" objects against the country centroid,
 *   - drive the "+ Add … here" quick-add flows so a freshly-created
 *     object lands inside the country the user is hovering.
 *
 * bbox and centroid are computed with Turf (`@turf/bbox`,
 * `@turf/center-of-mass`) so the centroid reflects the polygon's true
 * centre of mass rather than the midpoint of the bounding box — which
 * for L-shaped or archipelago countries (e.g. Indonesia, Norway) could
 * land in the ocean or a neighbour. Tolerant of mixed Polygon /
 * MultiPolygon geometries and bad coordinates (returns null).
 */

import turfBbox from '@turf/bbox';
import centerOfMass from '@turf/center-of-mass';
import type { Feature, Geometry } from 'geojson';

export interface CountryBounds {
  /** [minLng, minLat, maxLng, maxLat] — same order as Mapbox bbox. */
  bbox: [number, number, number, number];
  /** The polygon's true centre of mass (via @turf/center-of-mass). */
  centroid: { lng: number; lat: number };
  /**
   * The matched country's raw GeoJSON geometry (Polygon /
   * MultiPolygon). Surfaced so callers can run point-in-polygon to
   * decide which spatial objects truly belong to this country
   * (the bbox query alone leaks neighbours across shared borders).
   */
  geometry: { type?: string; coordinates?: unknown };
}

interface CountriesGeoJson {
  features?: Array<{
    id?: string | number;
    properties?: {
      ISO_NUMERIC?: string | number;
      ISO2?: string;
      ISO_A2?: string;
      [extra: string]: unknown;
    };
    geometry?: {
      type?: string;
      coordinates?: unknown;
    };
  }>;
}

/**
 * Find a country in the FeatureCollection by either its numeric ISO
 * code (preferred — matches the resolver upstream) or its ISO 3166-1
 * alpha-2 code. Returns null if no feature matches or the geometry is
 * malformed.
 */
export function getCountryBounds(
  countriesGeoJson: CountriesGeoJson | null | undefined,
  match: { code?: number | null; iso2?: string | null },
): CountryBounds | null {
  const features = countriesGeoJson?.features;
  if (!Array.isArray(features) || features.length === 0) return null;

  const wantCode = typeof match.code === 'number' && Number.isFinite(match.code) ? match.code : null;
  const wantIso2 = match.iso2 ? match.iso2.toUpperCase() : null;

  const feature = features.find((f) => {
    const props = f.properties ?? {};
    const fCode = typeof props.ISO_NUMERIC === 'number'
      ? props.ISO_NUMERIC
      : (typeof props.ISO_NUMERIC === 'string' && props.ISO_NUMERIC ? Number(props.ISO_NUMERIC) : null);
    if (wantCode != null && fCode != null && fCode === wantCode) return true;
    const iso2 = (typeof props.ISO2 === 'string' && props.ISO2)
      ? props.ISO2
      : (typeof props.ISO_A2 === 'string' && props.ISO_A2)
        ? props.ISO_A2
        : null;
    if (wantIso2 && iso2 && iso2.toUpperCase() === wantIso2) return true;
    return false;
  });

  if (!feature?.geometry?.coordinates) return null;

  try {
    const turfFeature: Feature<Geometry> = {
      type: 'Feature',
      properties: {},
      geometry: feature.geometry as Geometry,
    };

    const raw = turfBbox(turfFeature) as number[];
    // @turf/bbox can emit a 3D bbox ([minX,minY,minZ,maxX,maxY,maxZ]) for
    // geometry carrying elevation; normalise back to 2D [W,S,E,N].
    const bbox: [number, number, number, number] = raw.length > 4
      ? [raw[0], raw[1], raw[3], raw[4]]
      : [raw[0], raw[1], raw[2], raw[3]];

    if (!bbox.every((n) => Number.isFinite(n))) return null;

    const com = centerOfMass(turfFeature);
    const [cLng, cLat] = com.geometry.coordinates as [number, number];
    // center-of-mass can drift outside concave shapes; fall back to the
    // bbox midpoint if Turf hands back a non-finite coordinate.
    const centroid = (Number.isFinite(cLng) && Number.isFinite(cLat))
      ? { lng: cLng, lat: cLat }
      : { lng: (bbox[0] + bbox[2]) / 2, lat: (bbox[1] + bbox[3]) / 2 };

    return {
      bbox,
      centroid,
      geometry: feature.geometry as { type?: string; coordinates?: unknown },
    };
  } catch {
    return null;
  }
}
